import { snapdom } from '@zumer/snapdom';
import { jsPDF } from 'jspdf';
import { getGiBfromMiB } from '../../lib/helpers';

export const exportSummaryAsCsv = (summaryData) => {
    const rows = [
        ['Metric', 'Value'],
        ['Total Inventory', (summaryData.count?.linux ?? 0) + (summaryData.count?.windows ?? 0)],
        ['Ready for Migration', (summaryData.ready?.linux ?? 0) + (summaryData.ready?.windows ?? 0)],
        [
            'OSM Opportunities (Total)',
            (summaryData.can_modernize_os?.linux ?? 0) + (summaryData.can_modernize_os?.windows ?? 0),
        ],
        ['OSM Opportunities (Windows)', summaryData.can_modernize_os?.windows ?? 0],
        ['OSM Opportunities (Linux)', summaryData.can_modernize_os?.linux ?? 0],
        ['Undersized by CPU', summaryData.undersized_by_cpu],
        ['Needed vCPUs', summaryData.total_needed_cpu],
        ['Undersized by RAM', summaryData.undersized_by_ram],
        ['Needed RAM (GiB)', Math.ceil(getGiBfromMiB(summaryData.total_needed_ram))],
        ['Oversized by CPU', summaryData.oversized_by_cpu],
        ['Potential CPU Savings', summaryData.total_unused_cpu],
        ['Oversized by RAM', summaryData.oversized_by_ram],
        ['Potential RAM Savings', Math.ceil(getGiBfromMiB(summaryData.total_unused_ram))],
        ['Oversized by Storage', summaryData.oversized_by_storage],
        ['Total Unused CPU (vCPUs)', summaryData.total_unused_cpu],
        ['Total Unused RAM (GiB)', Math.floor(getGiBfromMiB(summaryData.total_unused_ram))],
        ['Total Unused Storage (GiB)', Math.floor(getGiBfromMiB(summaryData.total_unused_storage))],
        [],
        ['Top 3 for CPU Reduction', 'Name', 'CPU Reduction (vCPUs)'],
        ...(summaryData.top_three_for_cpu_reduction ?? []).map((m) => ['', m.name, m.cpu_total - m.cpu_used]),
        [],
        ['Top 3 for Rightsizing', 'Name', 'Storage Savings (GiB)'],
        ...(summaryData.top_three_for_right_sizing ?? []).map((m) => [
            '',
            m.name,
            Math.floor(getGiBfromMiB(m.total_free_space)),
        ]),
    ];

    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory-summary.csv';
    a.click();
    URL.revokeObjectURL(url);
};

export const exportSummaryAsPdf = async (element) => {
    const exportWidth = 926;

    const inventoryGrid = element.querySelector('.inventory-cards-grid');
    const detailsGrid = element.querySelector('.summary-details-grid');

    const saved = {
        width: element.style.width,
        minWidth: element.style.minWidth,
        border: element.style.border,
        padding: element.style.padding,
        inventoryGridCols: inventoryGrid?.style.gridTemplateColumns,
        detailsGridCols: detailsGrid?.style.gridTemplateColumns,
        inventoryCardCols: [...element.querySelectorAll('.inventory-cards-grid > *')].map((c) => c.style.gridColumn),
    };

    element.style.width = `${exportWidth}px`;
    element.style.minWidth = `${exportWidth}px`;
    element.style.border = 'none';
    element.style.padding = '0';
    if (inventoryGrid) inventoryGrid.style.gridTemplateColumns = 'repeat(2, 1fr)';
    if (detailsGrid) detailsGrid.style.gridTemplateColumns = 'repeat(2, 1fr)';
    element.querySelectorAll('.inventory-cards-grid > *').forEach((c) => (c.style.gridColumn = ''));

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const result = await snapdom(element, {
        scale: 2,
        embedFonts: true,
        exclude: ['.summary-wrapper-controls', '.summary-title-and-tooltip'],
        excludeMode: 'hide',
    });

    element.style.width = saved.width;
    element.style.minWidth = saved.minWidth;
    element.style.border = saved.border;
    element.style.padding = saved.padding;
    if (inventoryGrid) {
        inventoryGrid.style.gridTemplateColumns = saved.inventoryGridCols;
    }
    if (detailsGrid) {
        detailsGrid.style.gridTemplateColumns = saved.detailsGridCols;
    }
    element
        .querySelectorAll('.inventory-cards-grid > *')
        .forEach((c, i) => (c.style.gridColumn = saved.inventoryCardCols[i]));

    const canvas = await result.toCanvas();

    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const createdDate = new Date().toLocaleDateString('en-US');

    let currentY = margin;

    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.src = '/img/logo/pdf-header-logo.jpg';
    const logoLoaded = await new Promise((resolve) => {
        logo.onload = () => resolve(true);
        logo.onerror = () => resolve(false);
        setTimeout(() => resolve(false), 2000);
    });

    if (logoLoaded) {
        const logoWidth = 90;
        const logoHeight = logoWidth * (logo.naturalHeight / logo.naturalWidth);
        pdf.addImage(logo, 'JPG', margin, currentY, logoWidth, logoHeight);
        currentY += logoHeight + 20;
    } else {
        currentY += 10;
    }

    pdf.setFontSize(18);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(120, 120, 120);
    pdf.text('Discovery Assessment', margin, currentY);
    currentY += 3;

    const contentHeight = (canvas.height / canvas.width) * contentWidth;
    pdf.addImage(canvas.toDataURL('image/jpg'), 'JPG', margin, currentY, contentWidth, contentHeight);

    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Created on: ${createdDate}`, pageWidth - margin, pageHeight - margin, { align: 'right' });

    pdf.save('discovery-assessment.pdf');
};
